import { Banner, Card, EditorStateAsHtml, Typography } from '@repo/ui';
import { useAnalytics } from 'contexts/analytics/AnalyticsProvider';
import { format, parse } from 'date-fns';
import { storePractitionerNoteView } from 'helpers';
import useCurrentKitAndTinyAccount from 'hooks/useCurrentKitAndTinyAccount';
import { useEffect, useRef } from 'react';
import useFeature from 'services/features/useFeature';
import { usePublishedPractitionerNote } from 'services/practitionerNote/usePublishedPractitionerNote';

import styles from './PractitionerNote.module.css';
import usePractitionerNoteWarning from './hooks/usePractitionerNoteWarning';

export default function PractitionerNote() {
  const { isFeatureEnabled } = useFeature('practitioner_note');
  const { currentKitId } = useCurrentKitAndTinyAccount();
  const { trackEvent } = useAnalytics();
  const hasTrackedView = useRef(false);

  const { shouldShowWarning, bannerWarning } = usePractitionerNoteWarning();

  const { data: practitionerNote, isLoading } = usePublishedPractitionerNote(
    currentKitId ?? null,
  );

  //Track 'Patient Note Viewed' event and store view timestamp once per page visit
  useEffect(() => {
    if (
      !isLoading &&
      practitionerNote &&
      practitionerNote.is_published &&
      currentKitId &&
      !hasTrackedView.current
    ) {
      // Track analytics event
      trackEvent('Patient Note Viewed', { kit_id: currentKitId });
      // Store view timestamp in localStorage
      storePractitionerNoteView(currentKitId);
      hasTrackedView.current = true;
    }
  }, [isLoading, practitionerNote, currentKitId, trackEvent]);

  if (!isFeatureEnabled) return null;
  if (isLoading) return null;
  if (!practitionerNote || !practitionerNote.is_published) return null;

  const practitionerName = practitionerNote.practitioner?.user
    ? `${practitionerNote.practitioner.user.first_name ?? ''} ${practitionerNote.practitioner.user.last_name ?? ''}`.trim()
    : practitionerNote.practitioner?.first_name &&
        practitionerNote.practitioner?.last_name
      ? `${practitionerNote.practitioner.first_name} ${practitionerNote.practitioner.last_name}`.trim()
      : 'Your Practitioner';

  const formattedDate = practitionerNote.published_at
    ? format(
        parse(practitionerNote.published_at, 'yyyy-MM-dd HH:mm:ss', new Date()),
        'MMM dd, yyyy, h:mm a',
      )
    : '';

  return (
    <Card variant='wrapper' className={styles.cardWrapper}>
      <div className={styles.practitionerNoteContainer}>
        <div className='flex flex-column gap-3'>
          {shouldShowWarning && (
            <Banner
              variant='simple'
              color='lightPurple'
              content={bannerWarning}
            />
          )}
          <Typography variant='heading-m'>From your practitioner:</Typography>
          <div className='flex flex-column gap-1'>
            <Typography variant='label'>{practitionerName}</Typography>
            {!!formattedDate && (
              <Typography variant='label' color='grey'>
                {formattedDate}
              </Typography>
            )}
          </div>
        </div>
        <hr />
        <EditorStateAsHtml
          getEditorStateFnOrKey={() => practitionerNote?.content ?? null}
          deps={[practitionerNote?.content]}
        />
      </div>
    </Card>
  );
}
